/**
 * Faturalar modülü — Firestore okuma ve eşleşme/taslak yazma.
 */

import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  ACTIVE_INVOICE_STATUSES,
  CUSTOMER_STATUS,
  DEFAULT_INVOICE_SETTINGS,
  INVOICE_AUDIT_TYPES,
  INVOICE_MATCH_TYPE,
  INVOICE_MATCH_THRESHOLDS,
  INVOICE_PERSISTED_MATCH_STATUS,
  INVOICE_SETTINGS_DOC_ID,
  INVOICE_STATUS,
  INVOICE_READ_LIMITS,
  KNOWN_BRANCHES,
  SHARED_COMPANY_SETTINGS_FIELDS,
  resolveBankingId,
  resolveMatchConfidence,
  toPersistedMatchStatus
} from './invoiceSchema';
import { mergeInvoiceProductCatalog } from './invoiceProductCatalog';
import {
  mergeInvoiceConditionRules
} from './invoiceConditionRules';
import { mergeAmountNearestProductRule } from './invoiceAmountProductRule';
import { mergeInvoiceRules } from './invoiceRules';
import {
  buildKrediKartiInvoiceIslem,
  KREDI_KARTI_MATCH_SOURCE,
  krediKartiGrupDocId,
  mergeKrediKartiRule
} from './invoiceKrediKartiRule';
import { BANK_PROVIDERS } from './bankingSchema';
import { mergeBranchBankMovements } from './bankingMovementUi';
import {
  buildBranchInvoiceNote,
  resolveDraftNoteOnOpen,
  ensureProfessionalDraftDefaults,
  finalizeProfessionalDraft,
  getIslemProductName,
  getIslemQuantity,
  getProfessionalDraftMissingLabels,
  isDraftCustomerComplete,
  isDraftReadyForSend,
  createEmptyLineItem,
  normalizeInvoiceDraft,
  normalizeIslemForInvoice,
  trimDraftTextFields
} from './invoiceDraftUtils';
import {
  resolveCounterpartyTcknFromBankMovement,
  resolveCounterpartyVknFromBankMovement
} from './bankMovementTaxId';

const mapDocs = (snapshot) => snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

const buildBankMovementQueries = (bankingId) => {
  const limitN = INVOICE_READ_LIMITS.bankMovements;
  const vakifQ = query(
    collection(db, 'subeler', bankingId, 'bankMovements'),
    orderBy('bookingDate', 'desc'),
    limit(limitN)
  );
  const garantiQ = query(
    collection(db, 'subeler', bankingId, 'bankProviders', BANK_PROVIDERS.GARANTI, 'bankMovements'),
    orderBy('bookingDate', 'desc'),
    limit(limitN)
  );
  return { vakifQ, garantiQ };
};

const fetchMergedBankMovements = async (bankingId) => {
  const { vakifQ, garantiQ } = buildBankMovementQueries(bankingId);
  const [vakifSnap, garantiSnap] = await Promise.all([getDocs(vakifQ), getDocs(garantiQ)]);
  return mergeBranchBankMovements(mapDocs(vakifSnap), mapDocs(garantiSnap), bankingId);
};

const formatMusteriAdi = (musteri) => {
  if (!musteri) return '';
  return `${musteri.isim || ''} ${musteri.soyisim || ''}`.trim();
};

const resolveFirestoreDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'object') {
    const sec = value.seconds ?? value._seconds;
    if (sec != null && Number.isFinite(Number(sec))) {
      const ns = Number(value.nanoseconds ?? value._nanoseconds ?? 0);
      return new Date(Number(sec) * 1000 + Math.floor(ns / 1e6));
    }
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Firestore undefined kabul etmez; JSON ile temizle. */
export const sanitizeSnapshot = (obj) => {
  if (!obj || typeof obj !== 'object') return {};
  return JSON.parse(JSON.stringify(obj));
};

const DOC_ID_MAX = 120;

/** Kısa, kararlı özet (djb2) — uzun kimlikleri daraltırken çakışmayı önler. */
const kisaOzet = (metin) => {
  let h = 5381;
  for (let i = 0; i < metin.length; i += 1) h = ((h << 5) + h + metin.charCodeAt(i)) >>> 0;
  return h.toString(36);
};

/**
 * Firestore belge kimliği üretir.
 *
 * İKİ TUZAK:
 *  1. Sondan kesmek benzersizlik ekini YOK EDİYORDU. Banka hareketi id'si (55)
 *     + "__multi__" + 4 işlem id'si (83) = 147 karakter; sınır 120. Yeniden
 *     eşleştirmede sona eklenen zaman damgası kesiliyor, yeni kimlik iptal
 *     edilmiş kaydınkiyle aynı çıkıyor ve "zaten mevcut" diye atlanıyordu.
 *  2. Sondan kesmek FARKLI işlem kümelerini de aynı kimliğe indirebilir.
 *
 * Bu yüzden sığmayan kimliklerde değişken kısım özete çevrilir, ek her zaman
 * korunur.
 */
const docIdYap = (base, { rematch = false } = {}) => {
  const ek = rematch ? `__${Date.now()}` : '';
  if (base.length + ek.length <= DOC_ID_MAX) return `${base}${ek}`;
  const ozet = kisaOzet(base);
  const yer = DOC_ID_MAX - ek.length - ozet.length - 1;
  return `${base.slice(0, Math.max(0, yer))}~${ozet}${ek}`;
};

const buildMatchDocId = (bankMovementId, islemId, { rematch = false } = {}) =>
  docIdYap(`${String(bankMovementId || '').trim()}__${String(islemId || '').trim()}`, { rematch });

const buildMultiMatchDocId = (bankMovementId, islemIds, { rematch = false } = {}) => {
  const sorted = [...(islemIds || [])].map(String).filter(Boolean).sort().join('_');
  return docIdYap(`${String(bankMovementId || '').trim()}__multi__${sorted}`, { rematch });
};

const buildMultiMovementMatchDocId = (bankMovementIds, islemId, { rematch = false } = {}) => {
  const sorted = [...(bankMovementIds || [])].map(String).filter(Boolean).sort().join('_');
  return docIdYap(`multibank__${sorted}__${String(islemId || '').trim()}`, { rematch });
};

/** Çok hareket ↔ çok işlem (grup) eşleşmesinin belge kimliği. */
const buildGroupMatchDocId = (bankMovementIds, islemIds, { rematch = false } = {}) => {
  const bank = [...(bankMovementIds || [])].map(String).filter(Boolean).sort().join('_');
  const islem = [...(islemIds || [])].map(String).filter(Boolean).sort().join('_');
  return docIdYap(`grup__${bank}__${islem}`, { rematch });
};

export const _docIdIcTest = { docIdYap, buildMatchDocId, buildMultiMatchDocId, buildMultiMovementMatchDocId, buildGroupMatchDocId, DOC_ID_MAX };

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const amountsEqual = (a, b, tolerance = 0.01) => Math.abs(Number(a) - Number(b)) < tolerance;

/**
 * Bir eşleşmenin kapsadığı TÜM banka hareketi id'leri.
 * Çok-hareket eşleşmelerinde `bankMovementIds` dolar; eski kayıtlarda tek id vardır.
 * Mükerrer kontrolü ve kilitleme bunu kullanmalı, yoksa aynı hareket ikinci kez faturalanır.
 */
export const getMatchBankMovementIds = (match) => {
  if (match?.bankMovementIds?.length) return match.bankMovementIds.map(String);
  if (match?.bankMovementId) return [String(match.bankMovementId)];
  return [];
};

export const getMatchIslemIds = (match) => {
  if (match?.islemIds?.length) return match.islemIds.map(String);
  if (match?.islemId) return [String(match.islemId)];
  return [];
};

export const sumIslemHavaleAmount = (islemler) =>
  (islemler || []).reduce((sum, row) => sum + Number(row?.havaleEft || 0), 0);

/** Aktif taslak mı (mükerrer kontrolü için). */
export function isActiveInvoiceMatch(match) {
  return ACTIVE_INVOICE_STATUSES.includes(match?.invoiceStatus);
}

/** Gönderime hazır yapılabilir mi (liste + detay aynı zenginleştirmeyi kullanır). */
export function canMarkInvoiceReady(match, sube, settings = null) {
  if (![INVOICE_STATUS.DRAFT, INVOICE_STATUS.ERROR].includes(match?.invoiceStatus)) return false;
  const draft = enrichMatchDraftForEvaluation(match, sube, settings);
  return isDraftReadyForSend(draft);
}

/**
 * Liste/filtre/detay için ortak taslak zenginleştirme:
 * banka TCKN/VKN + şube il/ilçe varsayılanları + işçilik ayarları.
 * Kaydetmeden sadece değerlendirme / form hazırlığı için.
 */
export function enrichMatchDraftForEvaluation(match, sube, settings = null) {
  if (!match) return normalizeInvoiceDraft({}, settings);
  const islemler =
    match.matchType === 'multi'
      ? (match.islemlerData || []).filter((row) => row?.id)
      : match.islemData?.id
        ? [match.islemData]
        : [];

  let draftSeed = { ...(match.invoiceDraft || {}) };
  const existingDigits = String(
    draftSeed.vknTckn || draftSeed.customerTc || draftSeed.customerTaxNo || ''
  ).replace(/\D/g, '');
  if (existingDigits.length !== 11 && existingDigits.length !== 10 && match?.bankMovementData) {
    const bankTckn = resolveCounterpartyTcknFromBankMovement(match.bankMovementData);
    const bankVkn = !bankTckn ? resolveCounterpartyVknFromBankMovement(match.bankMovementData) : '';
    if (bankTckn) {
      draftSeed = { ...draftSeed, vknTckn: bankTckn, customerTc: bankTckn };
    } else if (bankVkn) {
      draftSeed = { ...draftSeed, vknTckn: bankVkn, customerTaxNo: bankVkn };
    }
  }

  return ensureProfessionalDraftDefaults(draftSeed, {
    sube: sube || match.sube,
    islem: islemler[0] || match.islemData || null,
    islemler,
    settings
  });
}

/**
 * @param {string} appSube - URL ?sube= değeri (alt çizgili)
 * @returns {string} banking Firestore şube id
 */
export const getBankingIdForAppSube = (appSube) => resolveBankingId(appSube);

export function faturaEslestirmeleriRef(sube) {
  return collection(db, 'subeler', sube, 'faturaEslestirmeleri');
}

export function invoiceSettingsRef(sube) {
  return doc(db, 'subeler', sube, 'invoiceSettings', INVOICE_SETTINGS_DOC_ID);
}

/**
 * Banka hareketini fatura kuyruğundan gizler (kaydı SİLMEZ).
 * arrayUnion kullanılıyor: iki kullanıcı aynı anda gizlerse biri diğerini ezmez.
 */
export async function hideBankMovementFromInvoicing(sube, movementId) {
  const id = String(movementId || '').trim();
  if (!sube || !id) throw new Error('Şube ve hareket gerekli');
  await setDoc(
    invoiceSettingsRef(sube),
    { hiddenBankMovementIds: arrayUnion(id), updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/** Gizlenen hareketi kuyruğa geri getirir. */
export async function unhideBankMovementFromInvoicing(sube, movementId) {
  const id = String(movementId || '').trim();
  if (!sube || !id) throw new Error('Şube ve hareket gerekli');
  await setDoc(
    invoiceSettingsRef(sube),
    { hiddenBankMovementIds: arrayRemove(id), updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export const mergeInvoiceSettings = (data) => ({
  ...DEFAULT_INVOICE_SETTINGS,
  ...(data || {}),
  nextInvoiceNo: Number(data?.nextInvoiceNo ?? DEFAULT_INVOICE_SETTINGS.nextInvoiceNo) || 1,
  enabled: Boolean(data?.enabled),
  testMode: data?.testMode !== false,
  apiPasswordSet: Boolean(data?.apiPasswordSet),
  apiSecretSet: Boolean(data?.apiSecretSet),
  workmanshipRates: {
    ...DEFAULT_INVOICE_SETTINGS.workmanshipRates,
    ...(data?.workmanshipRates || {})
  },
  productCatalog: mergeInvoiceProductCatalog(data?.productCatalog),
  invoiceConditionRules: mergeInvoiceConditionRules(data?.invoiceConditionRules, data),
  amountNearestProductRule: mergeAmountNearestProductRule(data?.amountNearestProductRule),
  krediKartiRule: mergeKrediKartiRule(data?.krediKartiRule),
  invoiceRules: mergeInvoiceRules(data?.invoiceRules),
  /**
   * Faturalandırılacak listesinden ELLE çıkarılan banka hareketleri.
   *
   * Banka kaydının kendisine dokunulmuyor — mutabakat, gün sonu ve finans
   * raporları o kayda bağlı. Yalnız fatura kuyruğunda görünmez oluyor; geri
   * almak da mümkün.
   */
  hiddenBankMovementIds: Array.isArray(data?.hiddenBankMovementIds)
    ? data.hiddenBankMovementIds.map(String).filter(Boolean)
    : [],
  price22AyarTakiPerGram: Number(data?.price22AyarTakiPerGram) || 0,
  cevrilenUrunIscilikYuzdesi: {
    ...DEFAULT_INVOICE_SETTINGS.cevrilenUrunIscilikYuzdesi,
    ...(data?.cevrilenUrunIscilikYuzdesi || {})
  },
  // Gram basina iscilik (KDV haric) — bos ise kod varsayilani (22 bilezik/takı 175, 14 ayar 250)
  gramBasiIscilikNet: { ...(data?.gramBasiIscilikNet || {}) },
  tamirIscilikNetTl:
    Number.isFinite(Number(data?.tamirIscilikNetTl)) && Number(data.tamirIscilikNetTl) >= 0
      ? Number(data.tamirIscilikNetTl)
      : DEFAULT_INVOICE_SETTINGS.tamirIscilikNetTl,
  milyem22Ayar: Number(data?.milyem22Ayar ?? DEFAULT_INVOICE_SETTINGS.milyem22Ayar) || 0.916,
  iscilik22AyarPerGram: Number(data?.iscilik22AyarPerGram) || 0,
  girisTamirMaxTl:
    Number(data?.girisTamirMaxTl ?? DEFAULT_INVOICE_SETTINGS.girisTamirMaxTl) ||
    DEFAULT_INVOICE_SETTINGS.girisTamirMaxTl,
  girisGramAltinMinTl:
    Number(data?.girisGramAltinMinTl ?? DEFAULT_INVOICE_SETTINGS.girisGramAltinMinTl) ||
    DEFAULT_INVOICE_SETTINGS.girisGramAltinMinTl,
  automationEnabled: data?.automationEnabled !== false,
  automaticMatching: data?.automaticMatching !== false,
  automaticDraft: data?.automaticDraft !== false,
  automaticApproval: Boolean(data?.automaticApproval),
  automaticSend: Boolean(data?.automaticSend),
  runMatchOnOpen: data?.runMatchOnOpen !== false,
  lowConfidenceRequiresManual: data?.lowConfidenceRequiresManual !== false,
  // Legacy düz metin secret'lar UI'ya taşınmaz
  apiPassword: '',
  apiSecret: ''
});

/** Fatura ayarlarını tek seferlik okur (okunamazsa varsayılanlara düşer). */
export async function loadInvoiceSettingsOnce(sube) {
  if (!sube) return mergeInvoiceSettings(null);
  try {
    const snap = await getDoc(invoiceSettingsRef(sube));
    return mergeInvoiceSettings(snap.exists() ? snap.data() : null);
  } catch (_) {
    return mergeInvoiceSettings(null);
  }
}

/** Audit trail satırı (ISO zaman — arrayUnion ile uyumlu) */
export function buildInvoiceAuditEvent(type, personel, detail = '') {
  return {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    at: new Date().toISOString(),
    type: String(type || ''),
    detail: String(detail || '').trim(),
    by: personel
      ? {
          id: personel.id || '',
          isim: personel.isim || '',
          soyisim: personel.soyisim || ''
        }
      : null
  };
}

export function subscribeInvoiceSettings(sube, onData, onError) {
  return onSnapshot(
    invoiceSettingsRef(sube),
    (snap) => onData(mergeInvoiceSettings(snap.exists() ? snap.data() : null)),
    (err) => onError?.(err)
  );
}

/** Client Firestore'a yazılmayacak hassas alanlar. */
const STRIP_SETTINGS_SECRETS = ['apiPassword', 'apiSecret'];

/**
 * Faturanın hukuki kimliğini belirleyen alanlar — firestore.rules bunları
 * admin dışına kilitler (invoiceSettingsIdentityUnchanged).
 */
const IDENTITY_SETTINGS_FIELDS = [
  'nextInvoiceNo',
  'invoicePrefix',
  'taxNumber',
  'taxOffice',
  'companyTitle',
  'mersisNo',
  'provider',
  'providerName',
  'enabled',
  'testMode',
  'apiBaseUrl',
  'apiUsername'
];

const sameSettingValue = (a, b) => {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === 'number' || typeof b === 'number') return Number(a) === Number(b);
  if (typeof a === 'boolean' || typeof b === 'boolean') return Boolean(a) === Boolean(b);
  return String(a ?? '') === String(b ?? '');
};

/**
 * Kimlik alanlarından GERÇEKTEN değişmeyenleri yazımdan çıkarır.
 *
 * NEDEN: ayar dokümanında bu alanlar hiç yoksa (yeni açılmış şube) kaydetme,
 * varsayılan değerleriyle hepsini ekliyordu. Kural bunu "kimlik alanı değişti"
 * sayıp şube personeline "Missing or insufficient permissions" veriyordu —
 * halbuki personel sadece bir fatura şartını düzenlemişti.
 *
 * Alan dokümanda yoksa ve yazılacak değer şema varsayılanıysa göndermeyiz;
 * okurken zaten mergeInvoiceSettings varsayılanı uyguluyor. Personel bu
 * alanlardan birini gerçekten değiştirirse alan gönderilir ve kural doğru
 * biçimde admin ister.
 */
const stripUnchangedIdentityFields = (payload, storedData) => {
  IDENTITY_SETTINGS_FIELDS.forEach((key) => {
    const stored = storedData ? storedData[key] : undefined;
    const next = payload[key];
    if (stored !== undefined) {
      if (sameSettingValue(stored, next)) delete payload[key];
      return;
    }
    if (sameSettingValue(DEFAULT_INVOICE_SETTINGS[key], next)) delete payload[key];
  });
  return payload;
};

const pickSharedCompanyFields = (source = {}) => {
  const out = {};
  SHARED_COMPANY_SETTINGS_FIELDS.forEach((key) => {
    if (source[key] !== undefined) out[key] = source[key];
  });
  return out;
};

export async function saveInvoiceSettings({ sube, settings, updatedByPersonel }) {
  if (!sube) throw new Error('Şube bilgisi zorunludur.');
  const incoming = { ...(settings || {}) };
  const merged = mergeInvoiceSettings(incoming);

  const passwordTyped = Boolean(String(settings?.apiPassword || '').trim());
  const secretTyped = Boolean(String(settings?.apiSecret || '').trim());

  const payload = sanitizeSnapshot({
    ...merged,
    enabled: Boolean(incoming?.enabled ?? merged.enabled),
    testMode: incoming?.testMode !== false,
    apiPasswordSet: passwordTyped || Boolean(merged.apiPasswordSet),
    apiSecretSet: secretTyped || Boolean(merged.apiSecretSet),
    providerConnectionStatus:
      incoming?.provider && incoming.provider !== 'none'
        ? incoming?.providerConnectionStatus || 'credentials_pending'
        : 'not_configured',
    updatedAt: serverTimestamp(),
    updatedByPersonel: sanitizeSnapshot(updatedByPersonel)
  });

  STRIP_SETTINGS_SECRETS.forEach((key) => {
    delete payload[key];
  });
  // Fatura sayacını yalnızca sunucu artırır; panelde açık kalan eski değer kaydedilince
  // sayaç geri gidip aynı numara ikinci kez kesilmesin
  delete payload.nextInvoiceNo;

  // Fatura şartları tek listeye (invoiceRules) taşındı. Eski kural alanları
  // veritabanından silindi; her kayıtta yeniden yazılmasınlar.
  ['invoiceConditionRules', 'krediKartiRule', 'amountNearestProductRule',
   'girisTamirMaxTl', 'girisGramAltinMinTl'].forEach((key) => {
    delete payload[key];
  });
  // GİB fatura no = 3 karakterlik seri + yıl + 9 hane.
  // e-Arşiv ve e-Fatura ayrı seri kullanıyor; üçü de aynı kurala tabi.
  ['invoicePrefix', 'invoicePrefixEArsiv', 'invoicePrefixEFatura'].forEach((key) => {
    if (payload[key] !== undefined) {
      payload[key] = String(payload[key] || '')
        .replace(/[^A-Za-z0-9]/g, '')
        .toUpperCase()
        .slice(0, 3);
    }
  });

  // Değişmeyen kimlik alanlarını gönderme (bkz. stripUnchangedIdentityFields)
  let storedData = null;
  try {
    const snap = await getDoc(invoiceSettingsRef(sube));
    storedData = snap.exists() ? snap.data() : null;
  } catch (_) {
    storedData = null;
  }
  stripUnchangedIdentityFields(payload, storedData);

  await setDoc(invoiceSettingsRef(sube), payload, { merge: true });

  // Firma bilgileri tüm şubelerde aynı
  const companySlice = sanitizeSnapshot({
    ...pickSharedCompanyFields(payload),
    updatedAt: serverTimestamp(),
    updatedByPersonel: sanitizeSnapshot(updatedByPersonel)
  });
  // Firma bilgisi tüm şubelerde aynı olmalı, ancak bu yazım yalnızca admin'de başarılı olur
  // (kurallar firma alanlarını admin'e kilitler). Eskiden hata allSettled ile yutuluyor,
  // kullanıcıya "kaydedildi" deniyor ve şubeler sessizce farklılaşıyordu.
  const otherBranches = KNOWN_BRANCHES.map((b) => b.key).filter((key) => key !== String(sube));
  const syncResults = await Promise.allSettled(
    otherBranches.map((branchKey) => setDoc(invoiceSettingsRef(branchKey), companySlice, { merge: true }))
  );
  const failedBranches = syncResults
    .map((result, index) => (result.status === 'rejected' ? otherBranches[index] : null))
    .filter(Boolean);

  return { ...payload, companySyncFailedBranches: failedBranches };
}

/**
 * Kart tutarı eşiği aşan işlemleri okur (kredi kartı faturası adayları).
 * Tek alanlı aralık sorgusu; bileşik indeks gerektirmez.
 */
/**
 * Kart tahsilatı olan satışlar.
 *
 * Eşik SORGUDA uygulanmaz. Çok parçalı işlemde eşik grubun kart toplamına
 * bakar; eşiğin altında kalan parça da faturaya girmeli. Sorgu eşikle
 * daraltılınca o parça hiç okunmuyordu (BERK AKAY 0994701: 11.340 TL'lik
 * parça faturaya girmedi, fatura 56.700 yerine 45.360 çıktı).
 *
 * Hacim küçük (01.10.2026: Kavaklı 131, Şefaköy 212 kart satışı), tamamını
 * okuyup eşiği kodda uygulamak güvenli.
 */
export async function fetchKrediKartiIslemler(sube) {
  if (!sube) return [];
  const q = query(collection(db, 'subeler', sube, 'islemler'), where('krediKarti', '>', 0));
  const snap = await getDocs(q);
  return mapDocs(snap).sort((a, b) => resolveTimestampMs(b) - resolveTimestampMs(a));
}

/**
 * Kredi kartı satışından fatura taslağı açar.
 *
 * Banka hareketi olmadığı için "sıfırdan fatura" kayıt biçimini kullanır
 * (firestore.rules bu biçime zaten izin veriyor; kural değişikliği gerekmez).
 * Belge kimliği işlemden türetildiği için aynı satışa ikinci taslak açılamaz.
 * Kayıt, işlemi havale eşleştirmesine KİLİTLEMEZ: karma ödemede havale kısmı
 * kendi banka hareketiyle ayrıca faturalanabilir.
 */
export async function createKrediKartiInvoiceDraft({
  sube,
  islem,
  islemler = null,
  createdByPersonel,
  invoiceSettings = null
}) {
  if (!sube) throw new Error('Şube bilgisi zorunludur.');
  // Çok parçalı işlemde grubun TAMAMI tek faturaya girer (bkz. groupKrediKartiIslemler).
  const grup = (islemler && islemler.length ? islemler : [islem]).filter((x) => x?.id);
  if (!grup.length) throw new Error('İşlem bilgisi zorunludur.');
  const personel = sanitizeSnapshot(createdByPersonel);
  if (!personel?.id) throw new Error('Personel oturumu zorunludur.');

  const settings = invoiceSettings || (await loadInvoiceSettingsOnce(sube));
  const rule = mergeKrediKartiRule(settings?.krediKartiRule);
  // Şart listesi tanımlıysa ürün tipine şartlar karar verir (bkz. invoiceRules).
  const sartVar = mergeInvoiceRules(settings?.invoiceRules).length > 0;
  const invoiceIslemler = grup.map((x) => sanitizeSnapshot(buildKrediKartiInvoiceIslem(x, rule, { sartVar })));
  const invoiceIslem = invoiceIslemler[0];

  const tabanDraft = await buildInvoiceDraftFromMatch({
    sube,
    bankMovement: null,
    islem: invoiceIslem,
    invoiceSettings: settings,
    paymentChannel: 'krediKarti'
  });
  const amount = round2(invoiceIslemler.reduce((t, x) => t + (Number(x.krediKarti) || 0), 0));
  // Tek parçada taban taslak zaten doğru; çok parçada kalemler yeniden üretilir.
  // (items boşaltılmazsa üretim atlanır — bkz. shouldAutoGenerateItems.)
  const draft =
    invoiceIslemler.length > 1
      ? finalizeProfessionalDraft(
          { ...tabanDraft, items: [], amount, autoGeneratedItems: true, manuallyEditedItems: false },
          { sube, islemler: invoiceIslemler, customerRecord: null, settings }
        )
      : tabanDraft;
  const customerStatus = evaluateCustomerStatus(draft);
  const docId = krediKartiGrupDocId(grup);

  const payload = sanitizeSnapshot({
    bankMovementId: 'standalone',
    bankMovementData: {},
    matchType: INVOICE_MATCH_TYPE.STANDALONE,
    invoiceSource: KREDI_KARTI_MATCH_SOURCE,
    islemId: 'standalone',
    islemData: invoiceIslem,
    krediKartiIslemId: String(grup[0].id),
    islemIds: grup.length > 1 ? grup.map((x) => String(x.id)) : [],
    islemlerData: grup.length > 1 ? invoiceIslemler : [],
    totalSelectedAmount: amount,
    selectedIslemCount: grup.length > 1 ? grup.length : 0,
    bankingId: getBankingIdForAppSube(sube),
    matchScore: 100,
    matchConfidence: resolveMatchConfidence(100),
    matchStatus: INVOICE_PERSISTED_MATCH_STATUS.MANUAL,
    manualMatched: false,
    autoMatched: true,
    manualMatchReason: `Kredi kartı satışı (${formatTlShort(amount)} > ${formatTlShort(rule.thresholdTl)})`,
    invoiceStatus: INVOICE_STATUS.DRAFT,
    customerStatus,
    invoiceDraft: draft,
    auditTrail: [
      buildInvoiceAuditEvent(
        INVOICE_AUDIT_TYPES.DRAFT_CREATED,
        personel,
        'Kredi kartı satışından otomatik taslak (22 Ayar Bilezik)'
      )
    ],
    createdByPersonel: personel,
    sube
  });
  payload.createdAt = serverTimestamp();
  payload.updatedAt = serverTimestamp();

  await createMatchDocOnce(sube, docId, payload);
  return { id: docId, ...payload, customerStatus, invoiceDraft: draft };
}

const formatTlShort = (n) => `${Number(n || 0).toLocaleString('tr-TR')} TL`;

/**
 * Banka hareketlerini tek seferlik okur (VakıfBank + Garanti birleşik).
 */
export async function fetchBankMovements(bankingId) {
  return fetchMergedBankMovements(bankingId);
}

/**
 * Havale/EFT içeren işlem kayıtlarını tek seferlik okur.
 */
export async function fetchHavaleIslemler(sube) {
  const q = query(collection(db, 'subeler', sube, 'islemler'), where('havaleEft', '>', 0));
  const snap = await getDocs(q);
  const rows = mapDocs(snap);
  return rows.sort((a, b) => resolveTimestampMs(b) - resolveTimestampMs(a));
}

export function subscribeBankMovements(bankingId, onData, onError) {
  const { vakifQ, garantiQ } = buildBankMovementQueries(bankingId);
  let vakifRows = [];
  let garantiRows = [];
  let vakifReady = false;
  let garantiReady = false;

  const emit = () => {
    if (!vakifReady || !garantiReady) return;
    onData(mergeBranchBankMovements(vakifRows, garantiRows, bankingId));
  };

  const u1 = onSnapshot(
    vakifQ,
    (snap) => {
      vakifRows = mapDocs(snap);
      vakifReady = true;
      emit();
    },
    (err) => onError?.(err)
  );

  const u2 = onSnapshot(
    garantiQ,
    (snap) => {
      garantiRows = mapDocs(snap);
      garantiReady = true;
      emit();
    },
    (err) => onError?.(err)
  );

  return () => {
    u1();
    u2();
  };
}

export function subscribeHavaleIslemler(sube, onData, onError) {
  const q = query(collection(db, 'subeler', sube, 'islemler'), where('havaleEft', '>', 0));
  return onSnapshot(
    q,
    (snap) => {
      const rows = mapDocs(snap);
      onData(rows.sort((a, b) => resolveTimestampMs(b) - resolveTimestampMs(a)));
    },
    (err) => onError?.(err)
  );
}

/**
 * Şubedeki tüm fatura eşleşmelerini okur.
 */
export async function getExistingInvoiceMatches(sube) {
  const q = query(faturaEslestirmeleriRef(sube), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return mapDocs(snap);
}

/**
 * Canlı fatura eşleşmeleri aboneliği.
 */
export function subscribeInvoiceMatches(sube, onData, onError) {
  const q = query(faturaEslestirmeleriRef(sube), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => onData(mapDocs(snap)),
    (err) => onError?.(err)
  );
}

export function gelenFaturalarRef(sube) {
  return collection(db, 'subeler', sube, 'gelenFaturalar');
}

/**
 * Gelen e-Faturalar aboneliği — seçili döneme göre.
 *
 * Arşiv 2023'e kadar geriye çekildikten sonra koleksiyon 1.300+ belgeye
 * çıktı; tamamını canlı dinlemek gereksiz. `donem` tek ay ('2026-10') ya da
 * ay listesi (Son 6 Ay) olabilir; verilmezse tamamı dinlenir (eski davranış).
 * issueDate 'YYYY-MM-DD' metni olduğu için aralık karşılaştırması sözlük
 * sırasıyla doğru çalışır.
 */
export function subscribeInboundInvoices(sube, donem, onData, onError) {
  const aylar = Array.isArray(donem) ? donem.filter(Boolean) : donem ? [donem] : [];
  let q;
  if (aylar.length) {
    const ilk = [...aylar].sort()[0];
    const sonAy = [...aylar].sort()[aylar.length - 1];
    q = query(
      gelenFaturalarRef(sube),
      where('issueDate', '>=', `${ilk}-01`),
      where('issueDate', '<=', `${sonAy}-32`),
      orderBy('issueDate', 'desc')
    );
  } else {
    q = query(gelenFaturalarRef(sube), orderBy('issueDate', 'desc'));
  }
  return onSnapshot(
    q,
    (snap) => onData(mapDocs(snap)),
    (err) => onError?.(err)
  );
}

/**
 * Eksik müşteri alanı etiketleri (UI uyarıları).
 */
export function getDraftMissingFieldLabels(invoiceDraft) {
  return getProfessionalDraftMissingLabels(normalizeInvoiceDraft(invoiceDraft || {}));
}

/**
 * Müşteri bilgisi tamlık kontrolü.
 */
export function evaluateCustomerStatus(invoiceDraft) {
  return recalculateDraftCustomerStatus(invoiceDraft);
}

/**
 * invoiceDraft üzerinden customerStatus hesaplar.
 */
export function recalculateDraftCustomerStatus(invoiceDraft) {
  const draft = normalizeInvoiceDraft(invoiceDraft || {});
  return isDraftCustomerComplete(draft) ? CUSTOMER_STATUS.COMPLETE : CUSTOMER_STATUS.MISSING;
}

/**
 * Liste/filtre için canlı müşteri durumu.
 * Eski kayıtlarda Firestore customerStatus bayrağı güncel olmayabilir;
 * şube varsayılan il/ilçe uygulanarak yeniden hesaplanır.
 */
export function resolveLiveCustomerStatus(match, sube, settings = null) {
  if (!match) return CUSTOMER_STATUS.MISSING;
  if (match.invoiceStatus === INVOICE_STATUS.CANCELLED) {
    return match.customerStatus || CUSTOMER_STATUS.MISSING;
  }
  const draft = enrichMatchDraftForEvaluation(match, sube, settings);
  return recalculateDraftCustomerStatus(draft);
}

/** Müşteri bilgisi tam + draft durumunda */
export function isReadyDraft(match, sube, settings = null) {
  return (
    resolveLiveCustomerStatus(match, sube, settings) === CUSTOMER_STATUS.COMPLETE &&
    match?.invoiceStatus === INVOICE_STATUS.DRAFT
  );
}

/** invoiceStatus ready */
export function isSendReadyDraft(match) {
  return match?.invoiceStatus === INVOICE_STATUS.READY;
}

export function filterDraftMatches(matches, filterKey, sube, settings = null) {
  const list = matches || [];
  // Ortak listede diger subenin kaydi kendi subesinin varsayilanlariyla
  // degerlendirilir (il/ilce vb.).
  const subeOf = (m) => m?._sube || sube;
  if (filterKey === 'pendingApproval') {
    return list.filter((m) => canMarkInvoiceReady(m, subeOf(m), settings));
  }
  if (filterKey === 'missing') {
    return list.filter(
      (m) =>
        m.invoiceStatus === INVOICE_STATUS.DRAFT &&
        resolveLiveCustomerStatus(m, subeOf(m), settings) === CUSTOMER_STATUS.MISSING
    );
  }
  if (filterKey === 'draft') {
    return list.filter((m) => m.invoiceStatus === INVOICE_STATUS.DRAFT);
  }
  if (filterKey === 'sendReady') {
    return list.filter((m) => m.invoiceStatus === INVOICE_STATUS.READY);
  }
  if (filterKey === 'sent') {
    return list.filter(
      (m) => m.invoiceStatus === INVOICE_STATUS.SENT || m.invoiceStatus === INVOICE_STATUS.APPROVED
    );
  }
  if (filterKey === 'cancelled') {
    return list.filter((m) => m.invoiceStatus === INVOICE_STATUS.CANCELLED);
  }
  if (filterKey === 'error') {
    return list.filter((m) => m.invoiceStatus === INVOICE_STATUS.ERROR);
  }
  return list;
}

const fetchCustomerRecord = async (sube, customerId) => {
  const id = String(customerId || '').trim();
  if (!id || !sube) return null;
  try {
    const snap = await getDoc(doc(db, 'subeler', sube, 'musteriler', id));
    return snap.exists() ? snap.data() : null;
  } catch (_) {
    return null;
  }
};

const resolveMatchIslemler = (match) => {
  if (match?.matchType === 'multi') {
    return (match?.islemlerData || []).filter((row) => row?.id);
  }
  return match?.islemData?.id ? [match.islemData] : [];
};

/**
 * Taslak detay panelinde gösterilecek formu hazırlar (UI state; kaydetmeden Firestore yazmaz).
 */
export async function prepareDraftFormForEditing({ sube, match, invoiceSettings }) {
  const customerId = String(match?.invoiceDraft?.customerId || match?.islemData?.musteri?.id || '').trim();
  const customerRecord = await fetchCustomerRecord(sube, customerId);
  const islemler = resolveMatchIslemler(match);

  let draftSeed = { ...(match?.invoiceDraft || {}) };
  const existingTc = String(draftSeed.vknTckn || draftSeed.customerTc || '').replace(/\D/g, '');
  if (existingTc.length !== 11 && match?.bankMovementData) {
    const bankTckn = resolveCounterpartyTcknFromBankMovement(match.bankMovementData);
    if (bankTckn) {
      draftSeed = { ...draftSeed, vknTckn: bankTckn, customerTc: bankTckn };
    }
  }
  draftSeed.invoiceNote = resolveDraftNoteOnOpen(draftSeed.invoiceNote, sube, draftSeed.paymentType);

  return ensureProfessionalDraftDefaults(draftSeed, {
    sube,
    customerRecord,
    islem: islemler[0] || match?.islemData || null,
    islemler,
    settings: invoiceSettings
  });
}

/**
 * Çoklu işlemden fatura taslağı üretir.
 */
export async function buildInvoiceDraftFromMultiMatch({ sube, bankMovement, islemler, source = 'manual-multi-match', invoiceSettings = null }) {
  const list = (islemler || []).filter((x) => x?.id);
  if (!list.length) throw new Error('Çoklu eşleştirme için en az bir işlem gerekir.');

  let settings = invoiceSettings;
  if (!settings && sube) {
    settings = await loadInvoiceSettingsOnce(sube);
  }

  const totalHavale = list.reduce((sum, row) => sum + (Number(row?.havaleEft) || 0), 0);
  // Taban taslak YALNIZ müşteri/meta bilgisi için kurulur; ilk işlemden üretilen
  // kalemleri taşımaz.
  //
  // Taşısaydı: kalemler bir kez üretildikten sonra sabit kaldığı için (bkz.
  // shouldAutoGenerateItems) aşağıdaki çağrı 4 işlemin kalemlerini üretmez ve
  // fatura yalnız ilk işlemi kapsardı. 100.000 TL'lik bir havalenin faturası
  // 44.000 TL çıkıyordu.
  const baseDraft = await buildInvoiceDraftFromMatch({ sube, bankMovement, islem: list[0], invoiceSettings: settings });
  return finalizeProfessionalDraft(
    {
      ...baseDraft,
      items: [],
      source,
      amount: totalHavale || Number(bankMovement?.amount) || 0,
      invoiceNote: buildBranchInvoiceNote(sube),
      autoGeneratedItems: true,
      manuallyEditedItems: false
    },
    { sube, islemler: list, customerRecord: null, settings }
  );
}

/**
 * Onaylanan eşleşmeden fatura taslağı üretir (müşteri kaydı varsa zenginleştirir).
 */
export async function buildInvoiceDraftFromMatch({
  sube,
  bankMovement,
  islem,
  invoiceSettings = null,
  paymentChannel = 'havale'
}) {
  const isKrediKarti = paymentChannel === 'krediKarti';
  let customerRecord = null;
  const musteriId = String(islem?.musteri?.id || '').trim();
  let settings = invoiceSettings;

  if (musteriId && sube) {
    customerRecord = await fetchCustomerRecord(sube, musteriId);
  }

  if (!settings && sube) {
    settings = await loadInvoiceSettingsOnce(sube);
  }

  const normalizedIslem = normalizeIslemForInvoice(islem, settings);

  const customerName =
    formatMusteriAdi(customerRecord || normalizedIslem?.musteri) || formatMusteriAdi(normalizedIslem?.musteri) || '';
  const invoiceDate =
    resolveFirestoreDate(normalizedIslem?.timestamp) ||
    resolveFirestoreDate(bankMovement?.bookingDate) ||
    new Date();

  const islemTc = String(normalizedIslem?.musteri?.tcKimlikNo || '').replace(/\D/g, '');
  const bankTckn = resolveCounterpartyTcknFromBankMovement(bankMovement);
  const bankVkn = resolveCounterpartyVknFromBankMovement(bankMovement);

  let poolTckn = '';
  let poolVkn = '';
  try {
    const { resolveIdentityFromPoolForDraft } = await import('./identityClient');
    const pool = await resolveIdentityFromPoolForDraft({
      sube,
      customerId: musteriId,
      bankMovement,
      customerName
    });
    if (pool?.usable) {
      poolTckn = pool.tckn || '';
      poolVkn = pool.vkn || '';
    }
  } catch (_) {
    /* kimlik servisi yoksa mevcut akış devam */
  }

  const custTc = String(customerRecord?.tcKimlikNo || '').replace(/\D/g, '');
  const custVkn = String(customerRecord?.vergiNo || '').replace(/\D/g, '');

  // Öncelik: kart → işlem → havuz (yüksek güven) → banka; çelişkide otomatik seçme
  let tc = custTc.length === 11 ? custTc : islemTc.length === 11 ? islemTc : '';
  if (!tc) {
    if (poolTckn && bankTckn && poolTckn !== bankTckn) {
      tc = '';
    } else {
      tc = poolTckn || bankTckn || '';
    }
  }

  let vkn = '';
  if (!tc) {
    vkn = custVkn.length === 10 ? custVkn : '';
    if (!vkn) {
      if (poolVkn && bankVkn && poolVkn !== bankVkn) vkn = '';
      else vkn = poolVkn || bankVkn || '';
    }
  }
  const customerType = vkn && !tc ? 'firma' : 'sahis';

  const taslak = finalizeProfessionalDraft(
    {
      customerName,
      customerId: musteriId || null,
      customerType,
      title: customerName,
      vknTckn: tc || vkn || '',
      customerTc: tc,
      customerTaxNo: vkn,
      customerPhone: customerRecord?.iletisimNo || null,
      address: customerRecord?.adres || null,
      customerAddress: customerRecord?.adres || null,
      city: customerRecord?.il || customerRecord?.sehir || null,
      customerIl: customerRecord?.il || customerRecord?.sehir || null,
      district: customerRecord?.ilce || null,
      customerIlce: customerRecord?.ilce || null,
      taxOffice: customerRecord?.vergiDairesi || null,
      customerTaxOffice: customerRecord?.vergiDairesi || null,
      country: 'Türkiye',
      invoiceType: 'ozelMatrah',
      matrahType: 'Özel Matrah',
      productName: getIslemProductName(normalizedIslem),
      quantity: getIslemQuantity(normalizedIslem),
      amount: isKrediKarti
        ? Number(normalizedIslem?.krediKarti) || 0
        : Number(normalizedIslem?.havaleEft) || Number(bankMovement?.amount) || 0,
      paymentType: isKrediKarti ? 'Kredi Kartı' : 'Havale/EFT',
      source: isKrediKarti ? 'auto-kredi-karti' : 'auto-match',
      invoiceDate: invoiceDate.toISOString(),
      invoiceNote: buildBranchInvoiceNote(sube, paymentChannel),
      isWaybillReplacement: true,
      autoGeneratedItems: true,
      manuallyEditedItems: false
    },
    { sube, islem: normalizedIslem, customerRecord, settings }
  );
  return applyKnownTaxpayerToDraft(taslak);
}

/**
 * Bilinen e-Fatura mukellefi (09.10.2026): Canli Banka'nin kullandigi
 * mukellefDurumu kaydi e-Fatura diyorsa taslak e-Fatura senaryosuyla olusur ve
 * bos vergi dairesi alicinin son faturasindan doldurulur. Onceden taslak
 * e-Arsiv gorunuyor, gonderimde e-Fatura olarak gidiyordu (ZAFER AYDIN).
 * Gonderimde tur yine Kolaysoft'tan canli sorgulanir; bu yalniz taslagi dogrular.
 */
export async function applyKnownTaxpayerToDraft(draft) {
  const id = String(draft?.vknTckn || '').replace(/\D/g, '');
  if (!draft || (id.length !== 10 && id.length !== 11)) return draft;
  try {
    const { getMukellefDurumu, MUKELLEF_DURUM } = await import('./mukellefDurumu');
    const durum = await getMukellefDurumu(id);
    if (durum?.durum !== MUKELLEF_DURUM.EFATURA) return draft;
    const sonuc = {
      ...draft,
      scenarioType: 'eFatura',
      recipientCheck: { ...(draft.recipientCheck || {}), isEfatura: true, identifier: id, source: 'mukellefDurumu' }
    };
    if (!String(draft.taxOffice || draft.customerTaxOffice || '').trim()) {
      const { fetchRecipientDetails } = await import('./recipientHistory');
      const gecmis = await fetchRecipientDetails(id);
      if (gecmis?.taxOffice) {
        sonuc.taxOffice = gecmis.taxOffice;
        sonuc.customerTaxOffice = gecmis.taxOffice;
      }
      if (gecmis?.street && !String(draft.address || draft.customerAddress || '').trim()) {
        sonuc.address = gecmis.street;
        sonuc.customerAddress = gecmis.street;
      }
    }
    return sonuc;
  } catch (_) {
    return draft;
  }
}

/**
 * customerId varsa müşteri kaydını merge ile günceller.
 */
export async function updateLinkedCustomerIfExists({ sube, invoiceDraft }) {
  const customerId = String(invoiceDraft?.customerId || '').trim();
  if (!customerId || !sube) return { updated: false };

  const fullName = String(invoiceDraft.customerName || '').trim();
  const parts = fullName.split(/\s+/).filter(Boolean);
  const soyisim = parts.length > 1 ? parts[parts.length - 1] : '';
  const isim = parts.length > 1 ? parts.slice(0, -1).join(' ') : fullName;

  const patch = sanitizeSnapshot({
    isim: isim || fullName,
    soyisim,
    tcKimlikNo: invoiceDraft.customerTc || null,
    iletisimNo: invoiceDraft.customerPhone || null,
    adres: invoiceDraft.customerAddress || null,
    il: invoiceDraft.customerIl || null,
    ilce: invoiceDraft.customerIlce || null,
    vergiNo: invoiceDraft.customerTaxNo || null,
    vergiDairesi: invoiceDraft.customerTaxOffice || null
  });

  await setDoc(doc(db, 'subeler', sube, 'musteriler', customerId), patch, { merge: true });
  return { updated: true, customerId };
}

/**
 * Fatura taslağını günceller; müşteri senkronu başarısız olursa ana kayıt korunur.
 */
export async function updateInvoiceDraft({
  sube,
  matchId,
  invoiceDraft,
  updatedByPersonel,
  invoiceStatus = INVOICE_STATUS.DRAFT
}) {
  if (!sube || !matchId) {
    throw new Error('Taslak güncellemesi için şube ve kayıt id zorunludur.');
  }

  const cleaned = sanitizeSnapshot(trimDraftTextFields(normalizeInvoiceDraft(invoiceDraft || {})));
  const customerStatus = recalculateDraftCustomerStatus(cleaned);

  await updateDoc(doc(db, 'subeler', sube, 'faturaEslestirmeleri', matchId), {
    invoiceDraft: cleaned,
    customerStatus,
    invoiceStatus,
    updatedAt: serverTimestamp(),
    updatedByPersonel: sanitizeSnapshot(updatedByPersonel)
  });

  let customerSyncError = null;
  try {
    await updateLinkedCustomerIfExists({ sube, invoiceDraft: cleaned });
  } catch (err) {
    customerSyncError = err;
  }

  return { customerStatus, invoiceDraft: cleaned, customerSyncError };
}

/**
 * Aktif mükerrer eşleşme kontrolü (cancelled hariç).
 * islemIdOrIds: tek id veya id listesi.
 */
export function findDuplicateInvoiceMatch(existingMatches, bankMovementIdOrIds, islemIdOrIds) {
  const bankIds = Array.isArray(bankMovementIdOrIds)
    ? bankMovementIdOrIds.map(String).filter(Boolean)
    : [String(bankMovementIdOrIds || '').trim()].filter(Boolean);
  const bankId = bankIds[0] || '';
  const islemIds = Array.isArray(islemIdOrIds)
    ? islemIdOrIds.map(String).filter(Boolean)
    : [String(islemIdOrIds || '').trim()].filter(Boolean);

  // Sıfırdan faturalar ortak placeholder id kullanır; mükerrer banka/işlem kontrolüne girmez.
  if (!bankId || bankId === 'standalone') return null;
  const realIslemIds = islemIds.filter((id) => id && id !== 'standalone');
  if (!realIslemIds.length) return null;

  return (existingMatches || []).find((row) => {
    if (!isActiveInvoiceMatch(row)) return false;
    if (row.matchType === INVOICE_MATCH_TYPE.STANDALONE) return false;
    const rowBankIds = getMatchBankMovementIds(row).filter((id) => id && id !== 'standalone');
    if (bankIds.some((id) => rowBankIds.includes(id))) return true;
    const rowIds = getMatchIslemIds(row).filter((id) => id && id !== 'standalone');
    return realIslemIds.some((id) => rowIds.includes(id));
  });
}

const resolveMatchDocId = (existing, bankMovementId, islemId) => {
  const baseId = buildMatchDocId(bankMovementId, islemId);
  const sameIdRow = (existing || []).find((m) => m.id === baseId);
  if (sameIdRow && !isActiveInvoiceMatch(sameIdRow)) {
    return buildMatchDocId(bankMovementId, islemId, { rematch: true });
  }
  return baseId;
};

/**
 * Eşleşme kaydını yalnızca yoksa yazar.
 * İki personel aynı anda Faturalar'ı açtığında ikisi de aynı çifti aday görüp
 * setDoc ile birbirinin kaydını eziyordu; transaction bunu tek yazıma indirir.
 */
async function createMatchDocOnce(sube, docId, payload) {
  const ref = doc(db, 'subeler', sube, 'faturaEslestirmeleri', docId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (snap.exists()) {
      const err = new Error('Bu banka hareketi veya işlem için zaten aktif taslak mevcut.');
      err.code = 'DUPLICATE_MATCH';
      err.existingMatch = { id: docId, ...snap.data() };
      throw err;
    }
    tx.set(ref, payload);
  });
}

/**
 * Eşleşmeyi onaylar ve fatura taslağı kaydı oluşturur.
 */
export async function createInvoiceMatch({
  sube,
  bankingId,
  bankMovement,
  islem,
  matchScore,
  matchStatus,
  createdByPersonel,
  manualMatched = false,
  manualMatchReason = '',
  autoMatched = false,
  draftSource = '',
  /** Toplu akışta bir kez okunup döngüye aktarılır; her kayıt için koleksiyon taranmaz. */
  existingMatches = null,
  invoiceSettings = null
}) {
  if (!sube || !bankMovement?.id || !islem?.id) {
    throw new Error('Eşleşme için banka hareketi ve işlem zorunludur.');
  }

  const existing = existingMatches || (await getExistingInvoiceMatches(sube));
  const duplicate = findDuplicateInvoiceMatch(existing, bankMovement.id, islem.id);
  if (duplicate) {
    const err = new Error('Bu banka hareketi veya işlem için zaten aktif taslak mevcut.');
    err.code = 'DUPLICATE_MATCH';
    err.existingMatch = duplicate;
    throw err;
  }

  const invoiceDraft = await buildInvoiceDraftFromMatch({ sube, bankMovement, islem, invoiceSettings });
  if (draftSource) invoiceDraft.source = draftSource;
  else if (autoMatched) invoiceDraft.source = 'system-auto-match';
  else if (manualMatched) invoiceDraft.source = 'manual-match';
  const customerStatus = evaluateCustomerStatus(invoiceDraft);
  const persistedMatchStatus =
    matchStatus ||
    (manualMatched ? INVOICE_PERSISTED_MATCH_STATUS.MANUAL : toPersistedMatchStatus(Number(matchScore) || 0));
  const docId = resolveMatchDocId(existing, bankMovement.id, islem.id);
  const personel = sanitizeSnapshot(createdByPersonel);
  const confidence = resolveMatchConfidence(matchScore);
  const auditType = autoMatched
    ? INVOICE_AUDIT_TYPES.AUTO_MATCHED
    : manualMatched
      ? INVOICE_AUDIT_TYPES.MANUAL_MATCHED
      : INVOICE_AUDIT_TYPES.DRAFT_CREATED;

  const payload = {
    bankMovementId: bankMovement.id,
    bankMovementData: sanitizeSnapshot(bankMovement),
    matchType: INVOICE_MATCH_TYPE.SINGLE,
    islemId: islem.id,
    islemData: sanitizeSnapshot(islem),
    islemIds: [islem.id],
    islemlerData: [sanitizeSnapshot(islem)],
    totalSelectedAmount: Number(islem.havaleEft) || Number(bankMovement.amount) || 0,
    selectedIslemCount: 1,
    bankingId: bankingId || getBankingIdForAppSube(sube),
    matchScore: Number(matchScore) || 0,
    matchConfidence: confidence,
    matchStatus: persistedMatchStatus,
    manualMatched: Boolean(manualMatched),
    autoMatched: Boolean(autoMatched),
    manualMatchReason: String(manualMatchReason || '').trim(),
    invoiceStatus: INVOICE_STATUS.DRAFT,
    customerStatus,
    invoiceDraft,
    auditTrail: [
      buildInvoiceAuditEvent(
        auditType,
        personel,
        autoMatched ? 'Otomatik eşleşme ile taslak oluşturuldu' : 'Fatura taslağı oluşturuldu'
      )
    ],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdByPersonel: personel,
    sube
  };

  await createMatchDocOnce(sube, docId, payload);

  return { id: docId, ...payload, customerStatus, invoiceDraft };
}

/**
 * Banka/işlem eşleşmesi olmadan sıfırdan fatura taslağı.
 */
export async function createStandaloneInvoiceDraft({ sube, createdByPersonel, invoiceDraft = null }) {
  if (!sube) throw new Error('Şube bilgisi zorunludur.');
  const personel = sanitizeSnapshot(createdByPersonel);
  if (!personel?.id) throw new Error('Personel oturumu zorunludur.');

  const seed = {
    ...(invoiceDraft || {}),
    source: 'manual-scratch',
    items:
      Array.isArray(invoiceDraft?.items) && invoiceDraft.items.length > 0
        ? invoiceDraft.items
        : [createEmptyLineItem()]
  };
  const draft = finalizeProfessionalDraft(normalizeInvoiceDraft(trimDraftTextFields(seed)), { sube });
  const customerStatus = evaluateCustomerStatus(draft);
  const docId = `standalone__${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  const payload = {
    bankMovementId: 'standalone',
    bankMovementData: {},
    matchType: INVOICE_MATCH_TYPE.STANDALONE,
    islemId: 'standalone',
    islemData: {},
    islemIds: [],
    islemlerData: [],
    totalSelectedAmount: 0,
    selectedIslemCount: 0,
    bankingId: getBankingIdForAppSube(sube),
    matchScore: 100,
    matchConfidence: resolveMatchConfidence(100),
    matchStatus: INVOICE_PERSISTED_MATCH_STATUS.MANUAL,
    manualMatched: true,
    autoMatched: false,
    manualMatchReason: 'Sıfırdan fatura',
    invoiceStatus: INVOICE_STATUS.DRAFT,
    customerStatus,
    invoiceDraft: draft,
    auditTrail: [
      buildInvoiceAuditEvent(INVOICE_AUDIT_TYPES.DRAFT_CREATED, personel, 'Eşleşmesiz sıfırdan fatura taslağı')
    ],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdByPersonel: personel,
    sube
  };

  await setDoc(doc(db, 'subeler', sube, 'faturaEslestirmeleri', docId), payload);
  return { id: docId, ...payload, customerStatus, invoiceDraft: draft };
}

/**
 * Düşük skorlu manuel eşleştirme.
 */
const resolveMultiMatchDocId = (existing, bankMovementId, islemIds) => {
  const baseId = buildMultiMatchDocId(bankMovementId, islemIds);
  const sameIdRow = (existing || []).find((m) => m.id === baseId);
  if (sameIdRow && !isActiveInvoiceMatch(sameIdRow)) {
    return buildMultiMatchDocId(bankMovementId, islemIds, { rematch: true });
  }
  return baseId;
};

/**
 * ÇOK banka hareketi + TEK işlem eşleştirmesi.
 *
 * Müşteri bir satışın bedelini birden fazla havaleyle gönderdiğinde kullanılır
 * (ör. 100.000 × 4 + 47.000 = 447.000). Kayıt, mevcut şemayı bozmadan yazılır:
 * `bankMovementId` ilk hareket (eski kodlar için), `bankMovementIds` hepsi.
 */
export async function createMultiMovementInvoiceMatch({
  sube,
  bankingId,
  bankMovements,
  islem,
  createdByPersonel,
  manualMatchReason = '',
  autoMatched = false,
  draftSource = 'multi-movement-match',
  existingMatches = null,
  invoiceSettings = null
}) {
  const list = (bankMovements || []).filter((m) => m?.id);
  if (!sube || !islem?.id || list.length < 2) {
    throw new Error('Çoklu hareket eşleştirmesi için işlem ve en az 2 banka hareketi zorunludur.');
  }

  const movementIds = list.map((m) => String(m.id));
  const totalMovementAmount = list.reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
  if (!amountsEqual(totalMovementAmount, Number(islem.havaleEft) || 0)) {
    throw new Error('Seçili hareketlerin toplamı işlem tutarıyla eşleşmiyor.');
  }

  const existing = existingMatches || (await getExistingInvoiceMatches(sube));
  const duplicate = findDuplicateInvoiceMatch(existing, movementIds, islem.id);
  if (duplicate) {
    const err = new Error('Bu hareketlerden biri veya işlem için zaten aktif taslak mevcut.');
    err.code = 'DUPLICATE_MATCH';
    err.existingMatch = duplicate;
    throw err;
  }

  const primary = list[0];
  const invoiceDraft = await buildInvoiceDraftFromMatch({
    sube,
    bankMovement: primary,
    islem,
    invoiceSettings
  });
  invoiceDraft.source = draftSource;

  const customerStatus = evaluateCustomerStatus(invoiceDraft);
  const personel = sanitizeSnapshot(createdByPersonel);
  const matchScore = autoMatched ? INVOICE_MATCH_THRESHOLDS.matched : 0;
  const baseId = buildMultiMovementMatchDocId(movementIds, islem.id);
  const sameIdRow = (existing || []).find((m) => m.id === baseId);
  const docId =
    sameIdRow && !isActiveInvoiceMatch(sameIdRow)
      ? buildMultiMovementMatchDocId(movementIds, islem.id, { rematch: true })
      : baseId;

  const payload = {
    // Eski kodlar tek id bekliyor; ilk hareket temsilci olarak yazılır.
    bankMovementId: primary.id,
    bankMovementData: sanitizeSnapshot(primary),
    bankMovementIds: movementIds,
    bankMovementsData: list.map((m) => sanitizeSnapshot(m)),
    matchType: INVOICE_MATCH_TYPE.SINGLE,
    multiMovement: true,
    islemId: islem.id,
    islemData: sanitizeSnapshot(islem),
    islemIds: [islem.id],
    islemlerData: [sanitizeSnapshot(islem)],
    totalSelectedAmount: Number(islem.havaleEft) || 0,
    totalMovementAmount,
    selectedIslemCount: 1,
    selectedMovementCount: list.length,
    bankingId: bankingId || getBankingIdForAppSube(sube),
    matchScore,
    matchConfidence: resolveMatchConfidence(matchScore),
    matchStatus: autoMatched
      ? INVOICE_PERSISTED_MATCH_STATUS.MATCHED
      : INVOICE_PERSISTED_MATCH_STATUS.MANUAL,
    manualMatched: !autoMatched,
    autoMatched: Boolean(autoMatched),
    manualMatchReason: String(manualMatchReason || '').trim(),
    invoiceStatus: INVOICE_STATUS.DRAFT,
    customerStatus,
    invoiceDraft,
    auditTrail: [
      buildInvoiceAuditEvent(
        autoMatched ? INVOICE_AUDIT_TYPES.AUTO_MATCHED : INVOICE_AUDIT_TYPES.MANUAL_MATCHED,
        personel,
        `${list.length} banka hareketi tek işlemle eşleştirildi`
      )
    ],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdByPersonel: personel,
    sube
  };

  await createMatchDocOnce(sube, docId, payload);
  return { id: docId, ...payload, customerStatus, invoiceDraft };
}

/**
 * ÇOK HAREKET ↔ ÇOK İŞLEM (grup eşleşmesi).
 *
 * Müşteri birden çok havale gönderip mağazada birden çok satış kaydı
 * oluştuğunda hiçbiri tek tek tutmuyor; sistem de yalnız "1 hareket ↔ N işlem"
 * ile "1 işlem ↔ N hareket" biliyordu, kayıtlar Faturalandırılacak'ta asılı
 * kalıyordu (30.10.2026, Kavaklı — MUHAMMED ÜSAME FETTAH: 3 havale 612.250 TL,
 * 4 satış 612.250 TL).
 *
 * Kayıt biçimi mevcut iki biçimin birleşimi: hem bankMovementIds hem islemIds
 * dolu. Toplamlar eşit olmadan kayıt açılmaz.
 */
export async function createGroupInvoiceMatch({
  sube,
  bankingId,
  bankMovements,
  islemler,
  createdByPersonel,
  manualMatchReason = '',
  autoMatched = false,
  draftSource = 'system-auto-group-match',
  existingMatches = null,
  invoiceSettings = null
}) {
  const hareketler = (bankMovements || []).filter((m) => m?.id);
  const islemList = (islemler || []).filter((x) => x?.id);
  if (!sube || hareketler.length < 1 || islemList.length < 1) {
    throw new Error('Grup eşleştirmesi için en az bir banka hareketi ve bir işlem gerekir.');
  }
  if (hareketler.length < 2 && islemList.length < 2) {
    throw new Error('Grup eşleştirmesi en az bir tarafta birden çok kayıt ister.');
  }

  const movementIds = hareketler.map((m) => String(m.id));
  const islemIds = islemList.map((x) => String(x.id));
  const totalMovementAmount = hareketler.reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
  const totalSelectedAmount = sumIslemHavaleAmount(islemList);
  if (!amountsEqual(totalMovementAmount, totalSelectedAmount)) {
    throw new Error('Hareket toplamı ile işlem toplamı eşleşmiyor.');
  }

  const existing = existingMatches || (await getExistingInvoiceMatches(sube));
  const duplicate = findDuplicateInvoiceMatch(existing, movementIds, islemIds);
  if (duplicate) {
    const err = new Error('Bu hareketlerden veya işlemlerden biri için zaten aktif taslak mevcut.');
    err.code = 'DUPLICATE_MATCH';
    err.existingMatch = duplicate;
    throw err;
  }

  const primary = hareketler[0];
  const first = islemList[0];
  const invoiceDraft = await buildInvoiceDraftFromMultiMatch({
    sube,
    bankMovement: primary,
    islemler: islemList,
    source: draftSource,
    invoiceSettings
  });
  const customerStatus = evaluateCustomerStatus(invoiceDraft);
  const personel = sanitizeSnapshot(createdByPersonel);
  const matchScore = autoMatched ? INVOICE_MATCH_THRESHOLDS.matched : 0;
  const baseId = buildGroupMatchDocId(movementIds, islemIds);
  const sameIdRow = (existing || []).find((m) => m.id === baseId);
  const docId =
    sameIdRow && !isActiveInvoiceMatch(sameIdRow)
      ? buildGroupMatchDocId(movementIds, islemIds, { rematch: true })
      : baseId;

  const payload = {
    // Eski ekranlar tek id bekliyor; ilk kayıtlar temsilci yazılır.
    bankMovementId: primary.id,
    bankMovementData: sanitizeSnapshot(primary),
    bankMovementIds: movementIds,
    bankMovementsData: hareketler.map((m) => sanitizeSnapshot(m)),
    matchType: INVOICE_MATCH_TYPE.MULTI,
    multiMovement: true,
    groupMatch: true,
    islemId: first.id,
    islemData: sanitizeSnapshot(first),
    islemIds,
    islemlerData: islemList.map((row) => sanitizeSnapshot(row)),
    totalSelectedAmount,
    totalMovementAmount,
    selectedIslemCount: islemList.length,
    selectedMovementCount: hareketler.length,
    bankingId: bankingId || getBankingIdForAppSube(sube),
    matchScore,
    matchConfidence: resolveMatchConfidence(matchScore),
    matchStatus: autoMatched
      ? INVOICE_PERSISTED_MATCH_STATUS.MATCHED
      : INVOICE_PERSISTED_MATCH_STATUS.MANUAL,
    manualMatched: !autoMatched,
    autoMatched: Boolean(autoMatched),
    manualMatchReason: String(manualMatchReason || '').trim(),
    invoiceStatus: INVOICE_STATUS.DRAFT,
    customerStatus,
    invoiceDraft,
    auditTrail: [
      buildInvoiceAuditEvent(
        autoMatched ? INVOICE_AUDIT_TYPES.AUTO_MATCHED : INVOICE_AUDIT_TYPES.MANUAL_MATCHED,
        personel,
        `${hareketler.length} banka hareketi ${islemList.length} işlemle grup olarak eşleştirildi`
      )
    ],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdByPersonel: personel,
    sube
  };

  await createMatchDocOnce(sube, docId, payload);
  return { id: docId, ...payload, customerStatus, invoiceDraft };
}

/**
 * Çoklu işlem + tek banka hareketi eşleştirmesi.
 */
export async function createMultiInvoiceMatch({
  sube,
  bankingId,
  bankMovement,
  islemler,
  createdByPersonel,
  manualMatchReason = '',
  autoMatched = false,
  draftSource = 'manual-multi-match',
  existingMatches = null,
  invoiceSettings = null
}) {
  const list = (islemler || []).filter((x) => x?.id);
  if (!sube || !bankMovement?.id || list.length < 2) {
    throw new Error('Çoklu eşleştirme için banka hareketi ve en az 2 işlem zorunludur.');
  }

  const islemIds = list.map((row) => row.id);
  const totalSelectedAmount = sumIslemHavaleAmount(list);
  if (!amountsEqual(totalSelectedAmount, bankMovement.amount)) {
    throw new Error('Seçili işlemlerin toplamı banka hareketiyle eşleşmiyor.');
  }

  const existing = existingMatches || (await getExistingInvoiceMatches(sube));
  const duplicate = findDuplicateInvoiceMatch(existing, bankMovement.id, islemIds);
  if (duplicate) {
    const err = new Error('Bu banka hareketi veya işlemlerden biri için zaten aktif taslak mevcut.');
    err.code = 'DUPLICATE_MATCH';
    err.existingMatch = duplicate;
    throw err;
  }

  const invoiceDraft = await buildInvoiceDraftFromMultiMatch({
    sube,
    bankMovement,
    islemler: list,
    source: draftSource,
    invoiceSettings
  });
  const customerStatus = evaluateCustomerStatus(invoiceDraft);
  const first = list[0];
  const docId = resolveMultiMatchDocId(existing, bankMovement.id, islemIds);
  const personel = sanitizeSnapshot(createdByPersonel);
  const matchScore = autoMatched ? INVOICE_MATCH_THRESHOLDS.matched : 0;

  const payload = {
    bankMovementId: bankMovement.id,
    bankMovementData: sanitizeSnapshot(bankMovement),
    matchType: INVOICE_MATCH_TYPE.MULTI,
    islemId: first.id,
    islemData: sanitizeSnapshot(first),
    islemIds,
    islemlerData: list.map((row) => sanitizeSnapshot(row)),
    totalSelectedAmount,
    selectedIslemCount: list.length,
    bankingId: bankingId || getBankingIdForAppSube(sube),
    matchScore,
    matchConfidence: resolveMatchConfidence(matchScore),
    matchStatus: autoMatched ? INVOICE_PERSISTED_MATCH_STATUS.MATCHED : INVOICE_PERSISTED_MATCH_STATUS.MANUAL,
    manualMatched: !autoMatched,
    autoMatched: Boolean(autoMatched),
    manualMatchReason: String(manualMatchReason || '').trim(),
    invoiceStatus: INVOICE_STATUS.DRAFT,
    customerStatus,
    invoiceDraft,
    auditTrail: [
      buildInvoiceAuditEvent(
        autoMatched ? INVOICE_AUDIT_TYPES.AUTO_MATCHED : INVOICE_AUDIT_TYPES.MANUAL_MATCHED,
        personel,
        `${list.length} işlem çoklu eşleştirildi`
      )
    ],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdByPersonel: personel,
    sube
  };

  await createMatchDocOnce(sube, docId, payload);
  return { id: docId, ...payload, customerStatus, invoiceDraft };
}

export async function createManualInvoiceMatch({
  sube,
  bankingId,
  bankMovement,
  islem,
  matchScore = 0,
  manualMatchReason = '',
  createdByPersonel
}) {
  return createInvoiceMatch({
    sube,
    bankingId,
    bankMovement,
    islem,
    matchScore,
    matchStatus: INVOICE_PERSISTED_MATCH_STATUS.MANUAL,
    createdByPersonel,
    manualMatched: true,
    manualMatchReason
  });
}

export async function markInvoiceDraftReady({ sube, matchId, updatedByPersonel, invoiceSettings = null }) {
  if (!sube || !matchId) throw new Error('Kayıt bilgisi eksik.');
  const ref = doc(db, 'subeler', sube, 'faturaEslestirmeleri', matchId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Taslak bulunamadı.');
  const data = snap.data();
  const settings = invoiceSettings || (await loadInvoiceSettingsOnce(sube));
  if (!canMarkInvoiceReady({ ...data, id: snap.id }, sube, settings)) {
    throw new Error('Taslak onaya gönderilemez. Müşteri ve ürün bilgilerini kontrol edin.');
  }
  await updateDoc(ref, {
    invoiceStatus: INVOICE_STATUS.READY,
    updatedAt: serverTimestamp(),
    updatedByPersonel: sanitizeSnapshot(updatedByPersonel),
    approvedAt: serverTimestamp(),
    auditTrail: arrayUnion(
      buildInvoiceAuditEvent(INVOICE_AUDIT_TYPES.APPROVED, updatedByPersonel, 'Onay kuyruğuna alındı')
    )
  });
  return { invoiceStatus: INVOICE_STATUS.READY };
}

/**
 * Seçili taslakları toplu onaya gönderir (invoiceStatus → ready).
 */
export async function markInvoiceDraftsReadyBatch({ sube, matchIds, updatedByPersonel }) {
  const ids = [...new Set((matchIds || []).map(String).filter(Boolean))];
  if (!sube || !ids.length) throw new Error('Onaya gönderilecek kayıt seçilmedi.');

  // Ayarlar tek sefer okunur; önceden her kayıt için yeniden çekiliyordu.
  const settings = await loadInvoiceSettingsOnce(sube);

  let ok = 0;
  const failed = [];
  // Toplu onay tek tıkla çalışır; 10'luk paketlerle paralel yürütülür.
  const CHUNK = 10;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    // eslint-disable-next-line no-await-in-loop
    const results = await Promise.allSettled(
      chunk.map((matchId) =>
        markInvoiceDraftReady({ sube, matchId, updatedByPersonel, invoiceSettings: settings })
      )
    );
    for (let j = 0; j < results.length; j += 1) {
      if (results[j].status === 'fulfilled') ok += 1;
      else failed.push({ matchId: chunk[j], message: results[j].reason?.message || 'Hata' });
    }
  }
  return { ok, failed, total: ids.length };
}

export async function revertInvoiceDraftToDraft({ sube, matchId, updatedByPersonel }) {
  if (!sube || !matchId) throw new Error('Kayıt bilgisi eksik.');
  await updateDoc(doc(db, 'subeler', sube, 'faturaEslestirmeleri', matchId), {
    invoiceStatus: INVOICE_STATUS.DRAFT,
    updatedAt: serverTimestamp(),
    updatedByPersonel: sanitizeSnapshot(updatedByPersonel),
    auditTrail: arrayUnion(
      buildInvoiceAuditEvent(INVOICE_AUDIT_TYPES.REVERTED, updatedByPersonel, 'Onay geri alındı')
    )
  });
  return { invoiceStatus: INVOICE_STATUS.DRAFT };
}


export async function cancelInvoiceDraft({ sube, matchId, cancelReason, cancelledByPersonel }) {
  const reason = String(cancelReason || '').trim();
  if (!sube || !matchId) throw new Error('Kayıt bilgisi eksik.');
  if (!reason) throw new Error('İptal nedeni zorunludur.');
  await updateDoc(doc(db, 'subeler', sube, 'faturaEslestirmeleri', matchId), {
    invoiceStatus: INVOICE_STATUS.CANCELLED,
    cancelReason: reason,
    cancelledAt: serverTimestamp(),
    cancelledByPersonel: sanitizeSnapshot(cancelledByPersonel),
    updatedAt: serverTimestamp(),
    updatedByPersonel: sanitizeSnapshot(cancelledByPersonel),
    auditTrail: arrayUnion(
      buildInvoiceAuditEvent(INVOICE_AUDIT_TYPES.CANCELLED, cancelledByPersonel, reason)
    )
  });
  return { invoiceStatus: INVOICE_STATUS.CANCELLED };
}

/**
 * Yalnızca iptal edilmiş fatura eşleşmesini siler.
 */
export async function deleteCancelledInvoiceMatch({ sube, matchId }) {
  if (!sube || !matchId) throw new Error('Kayıt bilgisi eksik.');

  const ref = doc(db, 'subeler', sube, 'faturaEslestirmeleri', matchId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Taslak bulunamadı.');

  const invoiceStatus = snap.data()?.invoiceStatus;
  if (invoiceStatus !== INVOICE_STATUS.CANCELLED) {
    throw new Error('Yalnızca iptal edilmiş taslaklar silinebilir.');
  }

  await deleteDoc(ref);
  return { deleted: true, matchId };
}

function resolveTimestampMs(row) {
  const raw = row?.timestamp || row?.bookingDate || row?.createdAt;
  if (!raw) return 0;
  if (typeof raw?.toDate === 'function') return raw.toDate().getTime();
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}
